import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { Person, PersonClient, PersonCreateModel, Role, RoleClient, Gender } from '../../utils/apiClient';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { FormPage, Field, ChipGroup, PersonFields } from '../../common/components/FormKit';
import './AdminParticipantModal.scss';

// PersonCreateModel не содержит email в сгенерированном клиенте — расширяем локально
interface PersonCreateModelWithEmail extends PersonCreateModel {
  email?: string;
}

interface CreatePersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPersonCreated: (person: Person) => void;
}

interface CreatePersonForm {
  lastName: string;
  firstName: string;
  patronymic: string;
  birthDate: Date;
  city: string;
  gender?: Gender;
  email: string;
  roleIds: number[];
}

const emptyForm = (): CreatePersonForm => ({
  lastName: '',
  firstName: '',
  patronymic: '',
  birthDate: new Date(),
  city: '',
  gender: undefined,
  email: '',
  roleIds: [],
});

const CreatePersonModal: React.FC<CreatePersonModalProps> = ({ isOpen, onClose, onPersonCreated }) => {
  const appContext = React.useContext(AppContext);
  const [form, setForm] = useState<CreatePersonForm>(emptyForm());
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createdPerson, setCreatedPerson] = useState<Person | null>(null);

  const personClient = new PersonClient(ApiUrl, axios);
  const roleClient = new RoleClient(ApiUrl, axios);

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm());
    setCreatedPerson(null);
    setError('');
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const data = await roleClient.getRoles();
        setRoles(data.filter(r => r.name !== 'Админ'));
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.gender) {
      setError('Укажите пол: он нужен для распределения по категориям.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload: PersonCreateModelWithEmail = Object.assign(
        new PersonCreateModel({
          lastName: form.lastName,
          firstName: form.firstName,
          patronymic: form.patronymic,
          birthDate: form.birthDate,
          city: form.city,
          gender: form.gender,
          roleIds: form.roleIds,
        }),
        { email: form.email.trim() || undefined }
      );
      const person = await personClient.createPerson(payload);
      setCreatedPerson(person);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterOnTournament = () => {
    if (createdPerson) onPersonCreated(createdPerson);
  };

  const handleClose = () => {
    setForm(emptyForm());
    setCreatedPerson(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="admin-participant-modal-overlay" onClick={handleClose}>
      <div className="admin-participant-modal" onClick={e => e.stopPropagation()}>
        <div className="admin-participant-modal__header">
          <h2>Новый участник</h2>
          <button className="admin-participant-modal__close" onClick={handleClose}>×</button>
        </div>

        <div className="admin-participant-modal__content">
          {loading ? (
            <div className="admin-participant-modal__loading">Загрузка...</div>
          ) : createdPerson ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div className="admin-participant-modal__success" style={{ marginBottom: '1.5rem' }}>
                Участник <strong>{createdPerson.lastName} {createdPerson.firstName}</strong> успешно создан!
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <button className="btn" onClick={handleRegisterOnTournament}>
                  Зарегистрировать на турнир
                </button>
                <button className="btn btn--back" onClick={handleClose}>
                  Закрыть
                </button>
              </div>
            </div>
          ) : (
            <FormPage title="Новый участник" variant="bare">
              <form className="form-page__form" onSubmit={handleSubmit}>
                {error && <div className="form-page__alert" role="alert">{error}</div>}

                <PersonFields
                  idPrefix="new-person"
                  value={form}
                  onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                  extraField={(
                    <Field label="Email" htmlFor="new-person-email" optional>
                      <input
                        id="new-person-email"
                        className="form-page__input"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                        autoComplete="off"
                        placeholder="name@example.com"
                      />
                    </Field>
                  )}
                />

                <ChipGroup
                  label="Роли"
                  type="checkbox"
                  name="new-person-roles"
                  emptyText="Список ролей недоступен"
                  options={roles.map((role) => ({ value: role.id!, label: role.name! }))}
                  selected={form.roleIds}
                  onToggle={(roleId, checked) => setForm((prev) => ({
                    ...prev,
                    roleIds: checked
                      ? [...prev.roleIds, Number(roleId)]
                      : prev.roleIds.filter((rid) => rid !== Number(roleId)),
                  }))}
                />

                <div className="form-page__actions">
                  <button type="button" className="btn btn--back" onClick={handleClose}>Отмена</button>
                  <button type="submit" className="btn form-page__submit" disabled={saving}>
                    {saving ? 'Сохранение…' : 'Создать участника'}
                  </button>
                </div>
              </form>
            </FormPage>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePersonModal;
